import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import { DataAccessFairsFacade } from '@planone/bau365/data-access-fair-data';
import * as DataAccessUser from '@planone/bau365/data-access-user';
import { WatchlistActionTrigger, WatchlistItemIdentity, WatchlistPageContext } from '@planone/bau365/data-access-user';
import { Event, Exhibitor, Post, Product } from '@planone/bau365/util-fair-data';
import * as FeatureTrackingGoogleAnalytics from '@planone/shared/feature-tracking-google-analytics';
import * as FeatureTrackingSnowplow from '@planone/shared/feature-tracking-snowplow';
import { debounceTime, filter, of, switchMap, withLatestFrom } from 'rxjs';

import * as WatchlistActions from './watchlist.actions';
import { WatchlistChangeInterface } from './watchlist.models';

import { DataTransformationHelper } from '../../helper/data-transformation.helpers';
import { CustomEventIglu } from '../../tracking-types/snowplow/schema/schema.interface';
import * as ShareActions from '../share/share.actions';

@Injectable()
export class WatchlistEffects {
  watchlistChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WatchlistActions.watchlistChange),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$),
      switchMap(([action, exclude_analytics]) => {
        const snowplowWatchlistChangeContext = this.dataTransformationHelper.snowplow?.mapArray('WATCHLIST', action);
        this.store.dispatch(
          FeatureTrackingSnowplow.selfDescribingEvent({
            event: {
              schema: CustomEventIglu.interact_with_watchlist,
              data: {
                interaction: action.interactionType,
                entity_type: action.contentType,
                context: action.context,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
              },
            },
            context: snowplowWatchlistChangeContext ? snowplowWatchlistChangeContext : undefined,
            enriched: true,
          })
        );

        let googleAnalyticsWatchlistEvent = 'add_to_watchlist';

        if (action.interactionType === 'REMOVE') {
          googleAnalyticsWatchlistEvent = 'delete_from_watchlist';
        }

        return of(
          FeatureTrackingGoogleAnalytics.push({
            event: googleAnalyticsWatchlistEvent,
            content_type: action.contentType,
            page_type: action.pageType,
            context: action.context,
            exclude_analytics: exclude_analytics,
            ...this.dataTransformationHelper.googleAnalytics?.map('WATCHLIST', action),
          })
        );
      })
    )
  );

  trackWatchlistItemSharing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUser.loadWatchlistEntitySuccess, DataAccessUser.loadWatchlistEntityFailure),
      filter((action) => action.trigger === WatchlistActionTrigger.SHARE),
      debounceTime(10),
      withLatestFrom(this.fairsFacade.trackingFairId$),
      switchMap(([enrichedAction, fairId]) => {
        return of(
          ShareActions.watchlistShareDialogOpened(
            this.buildGeneralizedWatchlistTrackingEvent(enrichedAction, fairId, 'SHARE')
          )
        );
      })
    )
  );

  trackActionWatchlistItemSharing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUser.loadWatchlistEntitySuccess, DataAccessUser.loadWatchlistEntityFailure),
      filter((action) => action.trigger === WatchlistActionTrigger.SHARE_ACTION),
      debounceTime(10),
      withLatestFrom(this.fairsFacade.trackingFairId$),
      switchMap(([enrichedAction, fairId]) => {
        return of(
          ShareActions.watchlistShareButtonClicked({
            change: this.buildGeneralizedWatchlistTrackingEvent(enrichedAction, fairId, 'SHARE'),
            network: enrichedAction.network ?? 'unknown',
          })
        );
      })
    )
  );

  trackWatchlistItemRemoval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUser.loadWatchlistEntitySuccess, DataAccessUser.loadWatchlistEntityFailure),
      filter((action) => action.trigger === WatchlistActionTrigger.REMOVE),
      debounceTime(10),
      withLatestFrom(this.fairsFacade.trackingFairId$),
      switchMap(([enrichedAction, fairId]) => {
        return of(
          WatchlistActions.watchlistChange(
            this.buildGeneralizedWatchlistTrackingEvent(enrichedAction, fairId, 'REMOVE')
          )
        );
      })
    )
  );

  trackWatchlistItemAdding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUser.loadWatchlistEntitySuccess, DataAccessUser.loadWatchlistEntityFailure),
      filter((action) => action.trigger === WatchlistActionTrigger.ADD),
      debounceTime(10),
      withLatestFrom(this.fairsFacade.trackingFairId$),
      switchMap(([enrichedAction, fairId]) => {
        return of(
          WatchlistActions.watchlistChange(this.buildGeneralizedWatchlistTrackingEvent(enrichedAction, fairId, 'ADD'))
        );
      })
    )
  );

  private fairId!: string;

  constructor(
    private readonly actions$: Actions,
    private store: Store,
    private authenticationFacade: AuthenticationFacade,
    private fairsFacade: DataAccessFairsFacade,
    private dataTransformationHelper: DataTransformationHelper
  ) {}

  private buildGeneralizedWatchlistTrackingEvent(
    action: {
      watchlistItem: WatchlistItemIdentity;
      pageContext: WatchlistPageContext;
      post?: Post;
      exhibitor?: Exhibitor;
      event?: Event;
      product?: Product;
      brand?: Exhibitor;
    },
    fairId: string,
    interaction: 'ADD' | 'REMOVE' | 'SHARE'
  ): WatchlistChangeInterface {
    return {
      interactionType: interaction,
      contentType: action.watchlistItem.entity_type,
      pageType: action.pageContext,
      context: fairId,
      ...('product' in action && { product: action.product }),
      ...('exhibitor' in action && { exhibitor: action.exhibitor }),
      ...('event' in action && { event: action.event }),
      ...('post' in action && { post: action.post }),
      ...('brand' in action && { brand: action.brand }),
    };
  }
}
